/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {FormEvent, MouseEvent, useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isArray from 'lodash.isarray';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {isArrayEmpty} from '@ion/core/src/utils/ArrayUtil';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {SearchScopeValues} from './types';
import {isExactSearchScope, SavedQueryType, SEARCH_QUERIES_PREFERENCE_NAME} from './utils';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export {SearchScopeValues} from './types';

interface SearchScopeProps {
  queryAttribute: string;
  setIsExact?: Function;
  isExact?: boolean;
  pageName: string;
  hasMultipleValues?: boolean;
};

const useStyles = makeStyles(() => {
  const toggleChip = {
    ...dlsAlt('typography', 'cta2'),
    borderRadius : 4,
    border       : 'none',
    padding      : '4px 8px',
    cursor       : 'pointer'
  };
  return {
    exactToggleChip : {
      ...toggleChip,
      color      : dlsColor('caGreen'),
      background : `${dlsColor('caGreen')}20`
    },
    partialToggleChip : {
      ...toggleChip,
      color      : dlsColor('grey3'),
      background : dlsColor('grey6')
    }
  };
});

export const getSavedSearchScopeForPage = (
  latestQueriesForPage: SavedQueryType,
  queryAttribute: string,
  pageName: string = ''
): boolean => (
  isExactSearchScope(latestQueriesForPage, queryAttribute, pageName)
);

const getSavedQueriesForPage = (pageName: string, isToggleClick: boolean = false) => {
  const userPreferences = UserPreferences.get(SEARCH_QUERIES_PREFERENCE_NAME) ?? [];
  if (isToggleClick) {
    return userPreferences?.filter(({pageName: name}: SavedQueryType) => name !== pageName);
  }
  return !isArrayEmpty(userPreferences)
    ? userPreferences?.filter(({pageName: name}) => name === pageName)[0] ?? {}
    : {};
};

/**
 * @SearchScope
 * Is a component that allows the user to search values partial/exact match.
 *
 * ```typescript
 * import SearchScope from './SearchScope';
 *
 * <SearchScope queryAttribute={queryAttribute}
 *   pageName={pageName} />
 *
 * ```
 *
 */
const SearchScope = ({
  pageName,
  setIsExact,
  queryAttribute,
  hasMultipleValues = false,
  isExact = false
}: SearchScopeProps) => {
  const classes = useStyles();
  const {
    on,
    off,
    exact
  } = translate('common:modularsearch.searchScope', {returnObjects: true});

  const readExactFromPrefs = (): boolean => {
    let scope = isExact;
    if (queryAttribute !== SearchScopeValues.JOBS) {
      scope = getSavedSearchScopeForPage(getSavedQueriesForPage(pageName), queryAttribute, pageName);
    }
    return scope;
  };

  const [isExactSearch, setIsExactSearch] = useState<boolean>(readExactFromPrefs);

  // Sync local state when the user switches to a different search attribute
  useEffect(() => {
    setIsExactSearch(readExactFromPrefs());
  }, [queryAttribute, pageName, isExact]);

  const onToggleClick = useCallback(() => {
    return async(event?: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!hasMultipleValues) {
        if (pageName === SearchScopeValues.JOBS) {
          setIsExact(!isExact);
        } else {
          const newIsExact       = !isExactSearch;
          const otherPageQueries = getSavedQueriesForPage(pageName, true);
          const queries          = newIsExact ? SearchScopeValues.EXACT : SearchScopeValues.PARTIAL;
          const latestPageQueries = getSavedQueriesForPage(pageName);
          const pageQueries      = latestPageQueries ?? {pageName, query: []};
          pageQueries.pageName   = pageName;
          if (!isArray(pageQueries.query)) {
            pageQueries.query = [];
          }
          const savedQueryIndex  = pageQueries.query.findIndex(({name}) => queryAttribute === name);
          if (savedQueryIndex >= 0) {
            pageQueries.query[savedQueryIndex].query = queries;
          } else {
            pageQueries.query.push({
              name  : queryAttribute,
              query : queries
            });
          }
          await UserPreferences.set(
            SEARCH_QUERIES_PREFERENCE_NAME,
            [...otherPageQueries, pageQueries],
            !UserPreferences.hasChangeDetection()
          );
          setIsExactSearch(newIsExact);
        }
      }
    };
  }, [
    queryAttribute,
    pageName,
    isExactSearch,
    isExact,
    hasMultipleValues,
    setIsExact]);

  return (
    <button type="button"
      className={(isExactSearch || hasMultipleValues) ? classes.exactToggleChip : classes.partialToggleChip}
      disabled={hasMultipleValues}
      onClick={onToggleClick()}>
      <Tooltip arrow title={!hasMultipleValues ? isExactSearch ? on : off: ''}>
        <span>
          {exact}
        </span>
      </Tooltip>
    </button>
  );
};

export default SearchScope;
